import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import {
  useAssociateSponsorMutation,
  useDisassociateSponsorMutation,
  useSponsorsForObjectQuery,
  useSponsorsQuery,
} from '@/queries/sponsors';
import { useFestivalWorkspaceStore } from '@/stores/festival-workspace-store';
import { Button } from '../../controls';
import Portal from '../../modal/Portal';

export function StageSponsorsGrid({ stageId }) {
  const festival = useFestivalWorkspaceStore((s) => s.festival);
  const festivalId = festival?.festivalId;

  const sponsorsQ = useSponsorsQuery(festivalId);
  const objectSponsorsQ = useSponsorsForObjectQuery(
    festivalId,
    'stage',
    stageId,
  );
  const associateMutation = useAssociateSponsorMutation(
    festivalId,
    'stage',
    stageId,
  );
  const disassociateMutation = useDisassociateSponsorMutation(
    festivalId,
    'stage',
    stageId,
  );

  const sponsors = sponsorsQ.data ?? [];
  const objectSponsors = objectSponsorsQ.data ?? [];
  const loading =
    sponsorsQ.isPending ||
    sponsorsQ.isFetching ||
    objectSponsorsQ.isPending ||
    objectSponsorsQ.isFetching;

  const [showModal, setShowModal] = useState(false);
  const [selectedSponsorId, setSelectedSponsorId] = useState('');

  const availableSponsors = sponsors.filter(
    (s) => !objectSponsors.some((sp) => sp.sponsorId === s.sponsorId),
  );

  const openModal = () => {
    setSelectedSponsorId('');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSponsorId('');
  };

  const handleSave = async () => {
    if (!selectedSponsorId) {
      return;
    }
    try {
      await associateMutation.mutateAsync({
        sponsorId: Number(selectedSponsorId),
      });
      closeModal();
    } catch {
      // mutation surfaces errors via query cache / UI elsewhere
    }
  };

  const canAdd = Boolean(stageId) && !loading && availableSponsors.length > 0;

  return (
    <div className="stage-sponsors">
      <div className="mb-3">
        <h4 className="m-0 text-sm font-bold uppercase tracking-[0.5px] text-[#121212]">
          Sponsors
        </h4>
      </div>

      {loading ? (
        <p className="m-0 text-sm text-[var(--muted-foreground)]">Loading…</p>
      ) : (
        <div className="stage-sponsors-grid">
          {objectSponsors.map((sponsor) => (
            <div key={sponsor.sponsorId} className="stage-sponsor-tile">
              {sponsor.photoURL ? (
                <img src={sponsor.photoURL} alt={sponsor.sponsorName || ''} />
              ) : (
                <div className="stage-sponsor-placeholder" aria-hidden>
                  <FontAwesomeIcon icon={['fas', 'handshake']} />
                </div>
              )}
              <div className="stage-sponsor-overlay">
                <button
                  type="button"
                  className="stage-sponsor-remove"
                  aria-label={`Remove ${sponsor.sponsorName || 'sponsor'}`}
                  title="Remove sponsor"
                  onClick={() =>
                    disassociateMutation
                      .mutateAsync(sponsor.sponsorId)
                      .catch(() => {})
                  }
                >
                  <FontAwesomeIcon icon={['fas', 'trash']} />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            className="stage-sponsor-tile stage-sponsor-add"
            onClick={openModal}
            disabled={!canAdd}
            aria-label="Add Sponsor"
            title={
              availableSponsors.length === 0
                ? 'No sponsors left to add'
                : 'Add Sponsor'
            }
          >
            <FontAwesomeIcon icon={['fas', 'plus']} />
            <span>Add</span>
          </button>
        </div>
      )}

      <Portal header="Add Sponsor" open={showModal} onClose={closeModal}>
        <div className="flex flex-col gap-6">
          <label className="flex flex-col gap-2">
            <span className="text-sm font-bold text-[#121212]">Sponsor</span>
            <select
              className="h-11 w-full max-w-md rounded-[var(--bethere-radius-md)] border border-[var(--input)] px-4 outline-none"
              value={selectedSponsorId}
              onChange={(e) => setSelectedSponsorId(e.target.value)}
              aria-label="Select sponsor to add"
            >
              <option value="">Select a sponsor…</option>
              {availableSponsors.map((sponsor) => (
                <option
                  key={sponsor.sponsorId}
                  value={String(sponsor.sponsorId)}
                >
                  {sponsor.sponsorName}
                </option>
              ))}
            </select>
          </label>

          <div className="flex flex-wrap justify-end gap-3">
            <Button type="button" mode="light" onClick={closeModal}>
              Cancel
            </Button>
            <Button
              type="button"
              mode="dark"
              onClick={handleSave}
              disabled={!selectedSponsorId || associateMutation.isPending}
            >
              <FontAwesomeIcon icon={['far', 'save']} />
              Save
            </Button>
          </div>
        </div>
      </Portal>
    </div>
  );
}
